import React, { useContext, useEffect, useState } from 'react'
import { RaisedButton, useSnackbar } from '@eduact/ed-components'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { MdContentCopy, MdCheck, MdAddPhotoAlternate, MdClose } from 'react-icons/md'
import { WalletContext } from '@Components/Wallet/WalletContext'
import { useAppSelector } from '@Hooks/ReduxHooks'
import { SnackbarConfig } from '@Utilities/SnackbarConfig'
import { manualVcApi, manualVcBase, ManualVcRequest } from './ManualVc.api'
import { clearManualDraft, readManualDraft, saveManualDraft } from './ManualVcDraft'

export default function ManualVcSubmission() {
	const { t } = useTranslation('wallet'),
		navigate = useNavigate(),
		{ openSnackbar } = useSnackbar()
	const text = (key: string) => t(`manual_vc.${key}`)
	const { walletIndex, closeWallet, setWalletIndex } = useContext(WalletContext)
	const userId = useAppSelector(s => s.auth.user?.uuid)
	const [request, setRequest] = useState<ManualVcRequest>(),
		[phone, setPhone] = useState('')
	const [file, setFile] = useState<File>(),
		[preview, setPreview] = useState('')
	const [error, setError] = useState(''),
		[busy, setBusy] = useState(false)
	const [copied, setCopied] = useState(false)
	const [uploadProgress, setUploadProgress] = useState<number>()
	const [previewLoading, setPreviewLoading] = useState(false)
	useEffect(() => {
		if (walletIndex !== 3 || !userId) return
		let alive = true
		setRequest(undefined)
		setError('')
		setFile(undefined)
		const saved = readManualDraft(userId)
		setPhone(saved?.phone || '')
		if (!saved?.reference) {
			setWalletIndex?.(2)
			return
		}
		manualVcApi
			.detail(saved.reference)
			.then(row => {
				if (!alive) return
				if (row.status !== 'draft') {
					clearManualDraft(userId)
					closeWallet?.()
					navigate(`${manualVcBase}?request=${row.reference}`)
				} else setRequest(row)
			})
			.catch(e => {
				if (alive) setError(e?.response?.data?.message || text('error'))
			})
		return () => {
			alive = false
		}
	}, [walletIndex, userId]) // eslint-disable-line react-hooks/exhaustive-deps
	useEffect(() => {
		const url = file ? URL.createObjectURL(file) : ''
		setPreviewLoading(Boolean(url))
		setPreview(url)
		return () => {
			if (url) URL.revokeObjectURL(url)
		}
	}, [file])
	const submit = async (e: React.FormEvent) => {
		e.preventDefault()
		if (!request || !file || busy || !userId) return
		setBusy(true)
		setUploadProgress(undefined)
		setError('')
		try {
			const row = await manualVcApi.submit(request.reference, phone, file, setUploadProgress)
			clearManualDraft(userId)
			openSnackbar({ ...SnackbarConfig.success, text: text('confirmation') })
			closeWallet?.()
			navigate(`${manualVcBase}?request=${row.reference}&submitted=1`)
		} catch (e: any) {
			setError(e?.response?.data?.message || text('error'))
		} finally {
			setBusy(false)
		}
	}
	if (walletIndex !== 3) return null
	return (
		<Form onSubmit={submit}>
			{error && <p role="alert">{error}</p>}
			{!request ? (
				<p role="status">{text('loading')}</p>
			) : (
				<>
					<Summary aria-label={text('payment_summary')}>
						<dl>
							<div>
								<dt>{text('credit')}</dt>
								<dd>
									<bdi>{request.amount} VC</bdi>
								</dd>
							</div>
							<div>
								<dt>{text('fee')}</dt>
								<dd>
									<bdi>
										{request.fee} {text('egp')}
									</bdi>
								</dd>
							</div>
							<div className="transfer-total">
								<dt>{text('total')}</dt>
								<dd>
									<bdi>
										{request.total} {text('egp')}
									</bdi>
								</dd>
							</div>
						</dl>
					</Summary>
					<Receiver>
						<span>{text('receiver')}</span>
						<div>
							<bdi>{request.receiver_number}</bdi>
							<CopyButton
								type="button"
								onClick={async () => {
									try {
										await navigator.clipboard.writeText(request.receiver_number)
										setCopied(true)
									} catch {
										setError(text('error'))
									}
								}}
							>
								{copied ? <MdCheck aria-hidden="true" /> : <MdContentCopy aria-hidden="true" />}
								<span aria-live="polite">{text(copied ? 'copied' : 'copy_short')}</span>
							</CopyButton>
						</div>
					</Receiver>
					<Instructions aria-label={text('how_to_pay')}>
						<h3>{text('how_to_pay')}</h3>
						<ol>
							<li>
								{text('transfer_instruction')}
								<small>{text('instapay')}</small>
							</li>
							<li>{text('proof_instruction')}</li>
							<li>{text('submit_instruction')}</li>
						</ol>
					</Instructions>
					<Field>
						<label htmlFor="manual-vc-phone">{text('phone')}</label>
						<PhoneInput
							disabled={busy}
							id="manual-vc-phone"
							type="tel"
							inputMode="tel"
							autoComplete="tel"
							required
							dir="ltr"
							placeholder="01XXXXXXXXX"
							aria-describedby="manual-vc-phone-hint"
							value={phone}
							onChange={e => {
								setPhone(e.target.value)
								const saved = userId && readManualDraft(userId)
								if (saved && userId) saveManualDraft(userId, { ...saved, phone: e.target.value })
							}}
						/>
						<small id="manual-vc-phone-hint">{text('phone_hint')}</small>
					</Field>
					<Field>
						<label htmlFor="manual-vc-proof">{text('screenshot')}</label>
						<UploadBox>
							<input
								disabled={busy}
								id="manual-vc-proof"
								required
								type="file"
								accept="image/jpeg,image/png,image/webp"
								aria-describedby="manual-vc-proof-hint"
								onChange={e => {
									const next = e.target.files?.[0]
									if (next && (!['image/jpeg', 'image/png', 'image/webp'].includes(next.type) || next.size > 10 * 1024 * 1024)) {
										setFile(undefined)
										e.target.value = ''
										setError(text('file_error'))
									} else if (next) {
										setFile(next)
										setError('')
									}
								}}
							/>
							<MdAddPhotoAlternate size={26} aria-hidden="true" />
							<strong>{text(file ? 'change_screenshot' : 'choose_screenshot')}</strong>
							<small>{text('file_hint')}</small>
						</UploadBox>
						{preview && (
							<Preview>
								{previewLoading && <span role="status">{text('loading_image')}</span>}
								<img
									key={preview}
									src={preview}
									alt={text('screenshot')}
									onLoad={() => setPreviewLoading(false)}
									onError={() => {
										setPreviewLoading(false)
										setError(text('file_error'))
										setFile(undefined)
									}}
								/>
								<span>{file?.name}</span>
								<RemoveButton
									disabled={busy}
									type="button"
									aria-label={text('remove_screenshot')}
									title={text('remove_screenshot')}
									onClick={() => {
										setFile(undefined)
										const input = document.getElementById('manual-vc-proof') as HTMLInputElement | null
										if (input) input.value = ''
									}}
								>
									<MdClose aria-hidden="true" />
								</RemoveButton>
							</Preview>
						)}
						{busy && (
							<div role="status" aria-live="polite">
								<span>
									{text(uploadProgress === 100 ? 'saving_request' : 'uploading_image')}
									{uploadProgress !== undefined && uploadProgress < 100 ? ` ${uploadProgress}%` : ''}
								</span>
								<progress
									aria-label={text('uploading_image')}
									max={100}
									value={uploadProgress === 100 ? undefined : uploadProgress}
									style={{ display: 'block', width: '100%', marginTop: 8 }}
								/>
							</div>
						)}
						<small id="manual-vc-proof-hint">{text('reload_file_hint')}</small>
					</Field>
					<RaisedButton type="submit" width="100%" disabled={busy || !file || !phone.trim()}>
						{busy ? text('loading') : text('submit')}
					</RaisedButton>
				</>
			)}
		</Form>
	)
}

const Form = styled.form`
	display: grid;
	gap: 18px;
	padding: 1rem 0;
	width: 100%;
	min-width: 0;
	font-size: 0.875rem;
	line-height: 1.55;
	p,
	h3 {
		margin: 0;
	}
	small {
		display: block;
		font-size: 0.75rem;
		line-height: 1.5;
		opacity: 0.85;
	}
	button,
	input {
		font-family: inherit;
	}
	[role='alert'] {
		color: #ffdca8;
	}
`
const Summary = styled.section`
	border: 1px solid rgba(255, 255, 255, 0.18);
	background: rgba(255, 255, 255, 0.06);
	border-radius: 12px;
	padding: 14px 16px;
	dl {
		display: grid;
		gap: 8px;
		margin: 0;
	}
	dl > div {
		display: flex;
		justify-content: space-between;
		align-items: baseline;
		gap: 16px;
	}
	dd {
		margin: 0;
		white-space: nowrap;
		font-weight: 600;
	}
	.transfer-total {
		border-top: 1px solid rgba(255, 255, 255, 0.2);
		padding-top: 10px;
		margin-top: 2px;
		font-weight: 700;
	}
	.transfer-total dd {
		font-size: 1.25rem;
	}
`
const Receiver = styled.div`
	display: grid;
	gap: 6px;
	> span {
		font-size: 0.75rem;
		opacity: 0.85;
	}
	> div {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 12px;
	}
	bdi {
		font-size: 1.25rem;
		font-weight: 700;
		letter-spacing: 0.04em;
	}
`
const CopyButton = styled.button`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 6px;
	flex-shrink: 0;
	min-height: 36px;
	padding: 6px 10px;
	border-radius: 8px;
	border: 1px solid rgba(255, 255, 255, 0.3);
	background: rgba(255, 255, 255, 0.08);
	color: inherit;
	font-size: 0.75rem;
	cursor: pointer;
	&:hover {
		background: rgba(255, 255, 255, 0.16);
	}
	&:focus-visible {
		outline: 2px solid ${({ theme }) => theme.colors.primary};
		outline-offset: 3px;
	}
`
const Instructions = styled.section`
	h3 {
		font-size: 0.875rem;
		font-weight: 700;
	}
	ol {
		margin: 8px 0 0;
		padding-inline-start: 28px;
		list-style: decimal outside;
		display: grid;
		gap: 12px;
	}
	li {
		display: list-item;
		list-style: decimal outside;
		padding-inline-start: 4px;
	}
	li::marker {
		font-weight: 700;
		color: ${({ theme }) => theme.colors.primary};
	}
	small {
		margin-top: 4px;
	}
`
const Field = styled.div`
	display: grid;
	gap: 7px;
	min-width: 0;
	> label {
		font-weight: 600;
	}
`
const PhoneInput = styled.input`
	width: 100%;
	box-sizing: border-box;
	min-width: 0;
	border: 1px solid rgba(255, 255, 255, 0.35);
	border-radius: 8px;
	padding: 12px;
	font-size: 1rem;
	line-height: 1.5;
	background: ${({ theme }) => theme.colors.light};
	color: ${({ theme }) => theme.colors.independence};
	&::placeholder {
		color: #6f7080;
		opacity: 1;
	}
	&:focus {
		outline: 2px solid ${({ theme }) => theme.colors.primary};
		outline-offset: 2px;
	}
`
const UploadBox = styled.div`
	position: relative;
	display: grid;
	justify-items: center;
	gap: 4px;
	padding: 14px 10px;
	border: 1px dashed rgba(255, 255, 255, 0.45);
	border-radius: 10px;
	background: rgba(255, 255, 255, 0.04);
	text-align: center;
	input {
		position: absolute;
		inset: 0;
		opacity: 0;
		width: 100%;
		height: 100%;
		cursor: pointer;
	}
	strong {
		font-size: 0.8125rem;
	}
	&:hover {
		background: rgba(255, 255, 255, 0.08);
	}
	&:focus-within {
		outline: 2px solid ${({ theme }) => theme.colors.primary};
		outline-offset: 2px;
	}
`
const Preview = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 8px;
	border-radius: 8px;
	background: rgba(255, 255, 255, 0.08);
	img {
		width: 64px;
		height: 64px;
		object-fit: contain;
		border-radius: 4px;
		background: white;
	}
	span {
		flex: 1;
		min-width: 0;
		overflow-wrap: anywhere;
		font-size: 0.75rem;
	}
`
const RemoveButton = styled(CopyButton)`
	width: 36px;
	padding: 6px;
`
